/** Dialed-style vertical silk strands (faithful port of their canvas wave). */

const F3 = 1 / 3
const G3 = 1 / 6
const floor = (n) => 0 | Math.floor(n)

const GRAD = [
  [1, 1, 0],
  [-1, 1, 0],
  [1, -1, 0],
  [-1, -1, 0],
  [1, 0, 1],
  [-1, 0, 1],
  [1, 0, -1],
  [-1, 0, -1],
  [0, 1, 1],
  [0, -1, 1],
  [0, 1, -1],
  [0, -1, -1],
]

const COLOR_STOPS = [
  { pos: 0, r: 242, g: 159, b: 0 }, // #F29F00
  { pos: 1 / 6, r: 255, g: 244, b: 230 }, // #FFF4E6
  { pos: 2 / 6, r: 41, g: 26, b: 2 }, // #291A02
  { pos: 3 / 6, r: 232, g: 153, b: 0 }, // #E89900
  { pos: 4 / 6, r: 175, g: 175, b: 176 }, // #AFAFB0
  { pos: 5 / 6, r: 180, g: 151, b: 104 }, // #B49768
  { pos: 1, r: 237, g: 182, b: 147 }, // #EDB693
]

export function gradientColor(t, alpha) {
  let i = 0
  for (let s = 1; s < COLOR_STOPS.length; s++) {
    if (t <= COLOR_STOPS[s].pos) {
      i = s - 1
      break
    }
  }
  const a = COLOR_STOPS[i]
  const b = COLOR_STOPS[i + 1] ?? a
  const u = a.pos === b.pos ? 0 : (t - a.pos) / (b.pos - a.pos)
  const r = Math.round(a.r + (b.r - a.r) * u)
  const g = Math.round(a.g + (b.g - a.g) * u)
  const bl = Math.round(a.b + (b.b - a.b) * u)
  return `rgba(${r},${g},${bl},${alpha.toFixed(3)})`
}

/** 3D simplex noise factory (matches Dialed). */
export function createNoise(rand = Math.random) {
  const perm = new Uint8Array(512)
  for (let i = 0; i < 256; i++) perm[i] = i
  for (let i = 0; i < 255; i++) {
    const j = i + ~~(rand() * (256 - i))
    const tmp = perm[i]
    perm[i] = perm[j]
    perm[j] = tmp
  }
  for (let i = 256; i < 512; i++) perm[i] = perm[i - 256]

  const gx = new Float64Array(512)
  const gy = new Float64Array(512)
  const gz = new Float64Array(512)
  for (let i = 0; i < 512; i++) {
    const g = GRAD[perm[i] % 12]
    gx[i] = g[0]
    gy[i] = g[1]
    gz[i] = g[2]
  }

  return (x, y, z) => {
    const s = (x + y + z) * F3
    const i = floor(x + s)
    const j = floor(y + s)
    const k = floor(z + s)
    const t = (i + j + k) * G3
    const x0 = x - (i - t)
    const y0 = y - (j - t)
    const z0 = z - (k - t)

    let i1
    let j1
    let k1
    let i2
    let j2
    let k2
    if (x0 >= y0) {
      if (y0 >= z0) {
        i1 = 1
        j1 = 0
        k1 = 0
        i2 = 1
        j2 = 1
        k2 = 0
      } else if (x0 >= z0) {
        i1 = 1
        j1 = 0
        k1 = 0
        i2 = 1
        j2 = 0
        k2 = 1
      } else {
        i1 = 0
        j1 = 0
        k1 = 1
        i2 = 1
        j2 = 0
        k2 = 1
      }
    } else if (y0 < z0) {
      i1 = 0
      j1 = 0
      k1 = 1
      i2 = 0
      j2 = 1
      k2 = 1
    } else if (x0 < z0) {
      i1 = 0
      j1 = 1
      k1 = 0
      i2 = 0
      j2 = 1
      k2 = 1
    } else {
      i1 = 0
      j1 = 1
      k1 = 0
      i2 = 1
      j2 = 1
      k2 = 0
    }

    const x1 = x0 - i1 + G3
    const y1 = y0 - j1 + G3
    const z1 = z0 - k1 + G3
    const x2 = x0 - i2 + 2 * G3
    const y2 = y0 - j2 + 2 * G3
    const z2 = z0 - k2 + 2 * G3
    const x3 = x0 - 1 + 3 * G3
    const y3 = y0 - 1 + 3 * G3
    const z3 = z0 - 1 + 3 * G3

    const ii = 255 & i
    const jj = 255 & j
    const kk = 255 & k

    let n0 = 0
    let t0 = 0.6 - x0 * x0 - y0 * y0 - z0 * z0
    if (t0 >= 0) {
      const gi = ii + perm[jj + perm[kk]]
      t0 *= t0
      n0 = t0 * t0 * (gx[gi] * x0 + gy[gi] * y0 + gz[gi] * z0)
    }

    let n1 = 0
    let t1 = 0.6 - x1 * x1 - y1 * y1 - z1 * z1
    if (t1 >= 0) {
      const gi = ii + i1 + perm[jj + j1 + perm[kk + k1]]
      t1 *= t1
      n1 = t1 * t1 * (gx[gi] * x1 + gy[gi] * y1 + gz[gi] * z1)
    }

    let n2 = 0
    let t2 = 0.6 - x2 * x2 - y2 * y2 - z2 * z2
    if (t2 >= 0) {
      const gi = ii + i2 + perm[jj + j2 + perm[kk + k2]]
      t2 *= t2
      n2 = t2 * t2 * (gx[gi] * x2 + gy[gi] * y2 + gz[gi] * z2)
    }

    let n3 = 0
    let t3 = 0.6 - x3 * x3 - y3 * y3 - z3 * z3
    if (t3 >= 0) {
      const gi = ii + 1 + perm[jj + 1 + perm[kk + 1]]
      t3 *= t3
      n3 = t3 * t3 * (gx[gi] * x3 + gy[gi] * y3 + gz[gi] * z3)
    }

    return 32 * (n0 + n1 + n2 + n3)
  }
}

const canvasMeta = new WeakMap()

export function setupCanvas(canvas) {
  if (!canvas) return null
  const dpr = window.devicePixelRatio || 1
  const w = canvas.clientWidth
  const h = canvas.clientHeight
  if (!w || !h) return null
  const ctx = canvas.getContext('2d')
  if (!ctx) return null
  const prev = canvasMeta.get(canvas)
  if (!prev || prev.w !== w || prev.h !== h || prev.dpr !== dpr) {
    canvas.width = w * dpr
    canvas.height = h * dpr
    canvasMeta.set(canvas, { w, h, dpr })
  }
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
  ctx.clearRect(0, 0, w, h)
  return { ctx, w, h }
}

/**
 * @param {function} noise - 3D simplex sampler
 * @param {CanvasRenderingContext2D} ctx
 * @param {number} w
 * @param {number} h
 * @param {number} normFreq - 0..1
 * @param {number} time - seconds
 * @param {number} opacityScale
 * @param {number} lineWidth
 * @param {number} layerKey - offsets noise layer (1 back / 2 overlay)
 * @param {number} dragIntensity
 * @param {'gradient' | 'white' | 'mixed'} colorMode - mixed = gold + white alternating strands
 */
export function drawStrandsVertical(
  noise,
  ctx,
  w,
  h,
  normFreq,
  time,
  opacityScale,
  lineWidth,
  layerKey,
  dragIntensity = 0,
  colorMode = 'gradient',
) {
  const shaped = Math.pow(Math.min(1, Math.max(0, normFreq)), 0.7)
  const maxSpread = w * (0.015 + 0.38 * shaped)
  const freqLo = 2 + 2.5 * shaped
  const freqHi = 3 + 4 * shaped
  const cx = w / 2
  const PI = Math.PI

  ctx.globalCompositeOperation = 'lighter'
  for (let i = 0; i < 24; i++) {
    const side = i / 23 - 0.5
    const strandOff = 2 * side * maxSpread
    const phase = 0.7 * side
    const sideAbs = 2 * Math.abs(side)
    const baseAlpha = (0.05 + 0.2 * sideAbs * sideAbs) * opacityScale
    const drag = 1 + 0.5 * dragIntensity

    ctx.lineWidth = lineWidth
    ctx.beginPath()
    for (let y = 0; y <= h; y += 2) {
      const v = y / h
      const envelope =
        0.35 + 0.65 * Math.pow(0.5 + 0.5 * Math.sin(3.2 * v * PI + 0.15 * time), 1.4)
      const n = 0.65 + 0.35 * noise(1.8 * v, 0.1 * layerKey, 0.12 * time)
      const twist =
        0.6 * Math.sin(v * freqLo * PI * 2 + 0.4 * time + phase) +
        0.4 * Math.sin(v * freqHi * PI * 2 + 0.22 * time + 0.5 * phase + 1)
      const x = cx + strandOff * envelope * n * twist * drag
      if (y === 0) ctx.moveTo(x, y)
      else ctx.lineTo(x, y)
    }
    const a = Math.min(baseAlpha * drag, 0.45 * opacityScale)
    const asWhite =
      colorMode === 'white' || (colorMode === 'mixed' && i % 2 === 1)
    ctx.strokeStyle = asWhite
      ? `rgba(255,255,255,${a.toFixed(3)})`
      : gradientColor(i / 23, a)
    ctx.stroke()
  }
  ctx.globalCompositeOperation = 'source-over'
}
